import React, { useState, useMemo } from 'react';
import { ClubEvent, Attendance, UserProfile } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { cleanIcsString } from '../lib/utils';
import { collection, doc, setDoc } from 'firebase/firestore';
import { format, parseISO, isAfter } from 'date-fns';
import { Calendar, MapPin, User, Clock, Check, X, HelpCircle, Search, Info, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from './ui/input';

interface MissionPlannerProps {
  events: ClubEvent[];
  attendance: Attendance[];
  userProfile: UserProfile | null;
  onTabChange?: (tab: string) => void;
  onSelectConvention?: (id: string | null) => void;
}

const MissionPlanner = React.memo(function MissionPlanner({ events, attendance, userProfile, onTabChange, onSelectConvention }: MissionPlannerProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUpcomingEvents = useMemo(() => {
    return events
      .filter(e => {
        if (!e.startDate) return false;
        const d = parseISO(e.startDate);
        if (isNaN(d.getTime())) return false;
        
        const compareDate = e.endDate ? parseISO(e.endDate) : d;
        // Set to end of day so events happening today remain visible
        compareDate.setHours(23, 59, 59, 999);

        return e.status !== 'completed' && 
        isAfter(compareDate, new Date()) &&
        e.type !== 'Monthly Report' &&
        e.type !== 'Department Report';
      })
      .filter(e => 
        searchTerm === '' || 
        e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.type.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime());
  }, [events, searchTerm]);

  const handleRSVP = async (eventId: string, rsvp: 'yes' | 'no' | 'maybe') => {
    if (!userProfile?.uid) return;

    try {
      const attendanceId = `${userProfile.uid}_${eventId}`;
      const attendanceRef = doc(db, 'attendance', attendanceId);
      
      await setDoc(attendanceRef, {
        userId: userProfile.uid,
        eventId: eventId,
        status: rsvp === 'yes' ? 'attending' : 'not-attending',
        rsvp: rsvp,
        timestamp: new Date().toISOString()
      }, { merge: true });

      toast.success(`RSVP set to ${rsvp.toUpperCase()}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'attendance');
    }
  };

  const handleConventionClick = (event: ClubEvent) => {
    if (onSelectConvention && onTabChange) {
      onSelectConvention(event.id!);
      onTabChange('con-planner');
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-blue pl-3 lg:pl-6 py-2">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 lg:mb-8">
          <div>
            <span className="text-xl lg:text-2xl font-bold text-lcars-blue uppercase tracking-tight">Mission Briefings</span>
            <p className="text-[0.55rem] lg:text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase mt-1">
              Active Duty Assignments and Requirements
            </p>
          </div>
          
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-lcars-blue" />
            <Input 
              placeholder="SEARCH OPERATIONS..." 
              className="w-full pl-10 bg-[var(--background)] border-2 border-lcars-blue text-[var(--foreground)] py-1.5 px-4 rounded-full text-[0.7rem] font-bold focus:bg-lcars-blue/10 outline-none transition-all placeholder:text-[var(--foreground)]/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredUpcomingEvents.length > 0 ? (
          <div className="space-y-4">
            {/* Headers */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 text-[0.65rem] font-bold text-lcars-blue uppercase tracking-widest border-b border-lcars-blue/20">
              <div className="col-span-4">Mission Name</div>
              <div className="col-span-2">Stardate</div>
              <div className="col-span-1">Time</div>
              <div className="col-span-3">Location</div>
              <div className="col-span-2 text-right">Confirm</div>
            </div>

            <div className="space-y-2">
              {filteredUpcomingEvents.map(event => {
                const userRSVP = attendance.find(a => a.eventId === event.id && a.userId === userProfile?.uid)?.rsvp;
                const isConvention = event.type === 'Convention';
                
                return (
                  <div key={event.id} className="group">
                    <div className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center p-3 md:p-4 bg-white/5 border-l-4 ${isConvention ? 'border-lcars-gold' : 'border-lcars-blue/40'} hover:bg-white/10 transition-all rounded-r-lg`}>
                      <div className="col-span-1 md:col-span-4 flex items-center gap-3 lg:gap-4">
                        <div className={`w-1.5 h-6 md:w-2 md:h-8 rounded-full shrink-0 ${isConvention ? 'bg-lcars-gold' : 'bg-lcars-blue'}`}></div>
                        <div>
                          <p className="font-bold text-base md:text-lg leading-tight uppercase tracking-tight group-hover:text-lcars-blue transition-colors truncate max-w-[200px] md:max-w-none">{cleanIcsString(event.name)}</p>
                          <p className="text-[0.55rem] md:text-[0.6rem] font-bold text-[var(--foreground)]/40 uppercase tracking-widest">{event.type}</p>
                        </div>
                      </div>

                      <div className="col-span-1 md:col-span-2 flex items-center gap-2 text-sm font-bold text-[var(--foreground)]/60">
                        <Calendar className="w-4 h-4 text-lcars-blue shrink-0" />
                        <span className="font-mono">{event.startDate ? format(parseISO(event.startDate), 'yyyy.MM.dd') : 'TBD'}</span>
                      </div>

                      <div className="col-span-1 md:col-span-1 flex items-center gap-2 text-sm font-bold text-[var(--foreground)]/60">
                        <Clock className="w-4 h-4 text-lcars-blue shrink-0" />
                        <span className="font-mono">{event.startDate ? format(parseISO(event.startDate), 'HH:mm') : 'TBD'}</span>
                      </div>

                      <div className="col-span-1 md:col-span-3 flex items-center gap-2 text-sm font-bold text-[var(--foreground)]/60">
                        <MapPin className="w-4 h-4 text-lcars-blue shrink-0" />
                        <a 
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cleanIcsString(event.location))}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate hover:text-lcars-blue transition-colors underline decoration-white/20 underline-offset-2"
                        >
                          {cleanIcsString(event.location) || 'SECTOR CLASSIFIED'}
                        </a>
                      </div>

                      <div className="col-span-1 md:col-span-2 flex flex-col gap-2">
                        {isConvention ? (
                          <button 
                            onClick={() => handleConventionClick(event)}
                            className="w-full h-10 lcars-pill bg-lcars-gold text-black flex items-center justify-center gap-2 text-[0.65rem] font-bold uppercase hover:bg-lcars-blue transition-all"
                          >
                            <Info className="w-4 h-4" /> ASSIGN SHIFTS <ChevronRight className="w-3 h-3" />
                          </button>
                        ) : (
                          <div className="flex gap-1 justify-end">
                            <button 
                              onClick={() => handleRSVP(event.id!, 'yes')}
                              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${userRSVP === 'yes' ? 'bg-lcars-blue text-black' : 'bg-white/5 text-[var(--foreground)]/40 hover:bg-white/10'}`}
                              title="ATTENDING"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleRSVP(event.id!, 'maybe')}
                              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${userRSVP === 'maybe' ? 'bg-lcars-tan text-black' : 'bg-white/5 text-[var(--foreground)]/40 hover:bg-white/10'}`}
                              title="TENTATIVE"
                            >
                              <HelpCircle className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleRSVP(event.id!, 'no')}
                              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${userRSVP === 'no' ? 'bg-lcars-red text-[var(--foreground)]' : 'bg-white/5 text-[var(--foreground)]/40 hover:bg-white/10'}`}
                              title="DECLINE"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-16 bg-white/5 rounded-r-full border-l-4 border-white/20">
            <p className="text-[var(--foreground)]/40 font-bold tracking-[0.3em] uppercase italic">
              {searchTerm ? 'No missions match your search parameters.' : 'No upcoming missions currently scheduled.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

export default MissionPlanner;
